/**
 * Role-based access control utilities for frontend
 */

// Role hierarchy (highest to lowest)
export const ROLE_HIERARCHY = [
  'organisation_management',
  'directors',
  'principal',
  'admin',
  'dean',
  'hr_manager',
  'student_mentors',
  'student_coordinators',
  'teachers',
  'teacher',
  'student',
  'parent'
];

// Dashboard path mapping for each role
export const ROLE_DASHBOARD_PATHS = {
  organisation_management: '/dashboard/org-management',
  directors: '/dashboard/directors',
  principal: '/dashboard/principal',
  admin: '/dashboard/admin',
  dean: '/dashboard/dean',
  hr_manager: '/dashboard/hr-manager',
  student_mentors: '/dashboard/student-mentors',
  student_coordinators: '/dashboard/student-coordinators',
  teachers: '/dashboard/teachers',
  teacher: '/dashboard/teachers',
  student: '/dashboard',
  parent: '/dashboard'
};

// Default fallback dashboard
export const DEFAULT_DASHBOARD = '/dashboard';

/**
 * Get the primary (highest) role from a list of roles
 */
export const getPrimaryRole = (roles = [], legacyRole = 'student') => {
  const roleSet = new Set(roles);
  return ROLE_HIERARCHY.find(r => roleSet.has(r)) || legacyRole;
};

/**
 * Get dashboard path for a given role
 */
export const getDashboardPath = (roles = [], legacyRole = 'student') => {
  const primary = getPrimaryRole(roles, legacyRole);
  return ROLE_DASHBOARD_PATHS[primary] || DEFAULT_DASHBOARD;
};

/**
 * Check if user has a specific role
 */
export const hasRole = (user, roleName) => {
  if (!user) return false;
  const roles = user.roles || [];
  return roles.includes(roleName);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user, roleNames = []) => {
  if (!user) return false;
  const roles = user.roles || [];
  return roleNames.some(r => roles.includes(r));
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (user, permissionName) => {
  if (!user) return false;
  const permissions = user.permissions || [];
  return permissions.includes(permissionName);
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (user, permissionNames = []) => {
  if (!user) return false;
  const permissions = user.permissions || [];
  return permissionNames.some(p => permissions.includes(p));
};

/**
 * Module visibility based on role
 */
export const VISIBLE_MODULES = {
  organisation_management: [
    'dashboard', 'students', 'staff', 'attendance', 'grades', 'fees', 'finance',
    'classes', 'courses', 'admissions', 'transport', 'assets',
    'library', 'frontoffice', 'facility', 'hr', 'boards',
    'audit', 'reports', 'settings'
  ],
  directors: [
    'dashboard', 'students', 'staff', 'finance', 'admissions', 'boards',
    'hr', 'audit', 'reports', 'settings'
  ],
  principal: [
    'dashboard', 'students', 'staff', 'attendance', 'grades', 'classes',
    'courses', 'admissions', 'transport', 'frontoffice',
    'facility', 'boards', 'reports'
  ],
  admin: [
    'dashboard', 'students', 'staff', 'attendance', 'grades', 'fees', 'finance',
    'classes', 'courses', 'admissions', 'transport', 'assets',
    'library', 'frontoffice', 'facility', 'hr', 'boards',
    'audit', 'reports', 'settings'
  ],
  dean: [
    'dashboard', 'students', 'attendance', 'grades', 'classes', 'courses',
    'boards', 'timetables', 'syllabi'
  ],
  hr_manager: [
    'dashboard', 'staff', 'attendance', 'grades', 'classes', 'courses',
    'frontoffice', 'hr', 'reports'
  ],
  student_mentors: [
    'dashboard', 'students', 'attendance', 'grades', 'frontoffice'
  ],
  student_coordinators: [
    'dashboard', 'students', 'attendance', 'transport', 'frontoffice'
  ],
  teachers: [
    'dashboard', 'students', 'attendance', 'grades', 'classes', 'courses'
  ],
  teacher: [
    'dashboard', 'students', 'attendance', 'grades', 'classes', 'courses'
  ],
  student: ['dashboard', 'students', 'attendance', 'grades', 'courses'],
  parent: ['dashboard', 'students', 'attendance', 'grades', 'fees']
};

/**
 * Check if a module is visible for a user's primary role
 */
export const isModuleVisible = (user, moduleName) => {
  if (!user) return false;
  const primary = getPrimaryRole(user.roles, user.role);
  const modules = VISIBLE_MODULES[primary] || [];
  return modules.includes(moduleName);
};
